import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../api';
import {
  UserPlus, Link2, Mail, Bell, Trash2, CheckCircle, Clock, AlertTriangle,
  Copy, ExternalLink, Users, FileText, Search, Eye, ShieldCheck, XCircle, Loader2
} from 'lucide-react';

const OnboardingAdmin = ({ user, setUser }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [creating, setCreating] = useState(false);
  const [empSearch, setEmpSearch] = useState('');

  // Detail dialog
  const [selectedInvite, setSelectedInvite] = useState(null);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await api.get('/onboarding/invites');
      const origin = window.location.origin;
      const enriched = res.data.map(inv => ({
        ...inv,
        portal_url: `${origin}/onboarding/${inv.token}`
      }));
      setInvites(enriched);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get('/employees');
      const emps = Array.isArray(res.data) ? res.data : res.data?.employees || [];
      setEmployees(emps);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchInvites(); fetchEmployees(); }, [fetchInvites, fetchEmployees]);

  const handleCreate = async () => {
    if (!selectedEmpId) { toast.error('Select an employee'); return; }
    setCreating(true);
    try {
      const res = await api.post('/onboarding/invites', {
        employee_id: selectedEmpId,
        send_email: sendEmail,
        frontend_origin: window.location.origin
      });
      toast.success(`Onboarding invite created${res.data.email_sent ? ' and email sent' : ''}`);
      setShowCreate(false);
      setSelectedEmpId('');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const handleRemind = async (id) => {
    try {
      const res = await api.put(`/onboarding/invites/${id}/remind`, { frontend_origin: window.location.origin });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this onboarding invite?')) return;
    try {
      await api.delete(`/onboarding/invites/${id}`);
      toast.success('Invite revoked');
      fetchInvites();
    } catch (err) {
      toast.error('Failed to revoke');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await api.put(`/onboarding/invites/${id}/approve`);
      toast.success(res.data.message);
      fetchInvites();
      setSelectedInvite(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const getStatusBadge = (inv) => {
    if (inv.status === 'approved') return <Badge className="bg-blue-100 text-blue-700">Approved</Badge>;
    if (inv.status === 'completed') return <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>;
    if (inv.status === 'revoked') return <Badge className="bg-red-100 text-red-700">Revoked</Badge>;
    if (inv.is_expired) return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    if (inv.status === 'in_progress') return <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>;
    return <Badge className="bg-slate-100 text-slate-700">Pending</Badge>;
  };

  const filteredInvites = invites.filter(inv => {
    if (filter !== 'all' && inv.status !== filter && !(filter === 'expired' && inv.is_expired)) return false;
    if (search) {
      const s = search.toLowerCase();
      return inv.employee_name?.toLowerCase().includes(s) || inv.employee_code?.toLowerCase().includes(s) || inv.employee_email?.toLowerCase().includes(s);
    }
    return true;
  });

  const stats = {
    total: invites.length,
    pending: invites.filter(i => i.status === 'pending' && !i.is_expired).length,
    inProgress: invites.filter(i => i.status === 'in_progress' && !i.is_expired).length,
    completed: invites.filter(i => i.status === 'completed').length,
    approved: invites.filter(i => i.status === 'approved').length,
    expired: invites.filter(i => i.is_expired).length,
  };

  // Employees not already invited (filter for create dialog)
  const invitedEmpIds = new Set(invites.filter(i => i.status !== 'revoked').map(i => i.employee_id));
  const availableEmployees = employees.filter(e => {
    if (invitedEmpIds.has(e.id)) return false;
    if (empSearch) {
      const s = empSearch.toLowerCase();
      const name = (e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim()).toLowerCase();
      return name.includes(s) || (e.employee_id || '').toLowerCase().includes(s) || (e.email || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="onboarding-admin-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employee Onboarding</h1>
            <p className="text-slate-500 text-sm">Send onboarding links to new hires and track their progress</p>
          </div>
          <Button onClick={() => { setShowCreate(true); setSelectedEmpId(''); setEmpSearch(''); }} data-testid="create-invite-btn">
            <UserPlus size={16} className="mr-2" /> New Onboarding
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-slate-400">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-700">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-400">
            <CardContent className="p-4">
              <p className="text-xs text-amber-600">Pending</p>
              <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-400">
            <CardContent className="p-4">
              <p className="text-xs text-blue-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-400">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-600">Completed</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.completed + stats.approved}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-4">
              <p className="text-xs text-red-600">Expired</p>
              <p className="text-2xl font-bold text-red-700">{stats.expired}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by name, code, or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invites Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 text-center text-slate-400">Loading...</div>
            ) : filteredInvites.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <UserPlus size={40} className="mx-auto mb-3 text-slate-300" />
                <p>No onboarding invites found</p>
                <p className="text-xs mt-1">Click "New Onboarding" to send an invite</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50 text-left text-slate-500">
                      <th className="p-3 font-medium">Employee</th>
                      <th className="p-3 font-medium">Department</th>
                      <th className="p-3 font-medium text-center">Progress</th>
                      <th className="p-3 font-medium text-center">Status</th>
                      <th className="p-3 font-medium">Expires</th>
                      <th className="p-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvites.map(inv => (
                      <tr key={inv.id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`invite-row-${inv.id}`}>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-slate-800">{inv.employee_name}</p>
                            <p className="text-xs text-slate-400">{inv.employee_code} {inv.employee_email ? `| ${inv.employee_email}` : ''}</p>
                          </div>
                        </td>
                        <td className="p-3 text-slate-600">{inv.department_name || '-'}</td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${inv.completion_percentage >= 100 ? 'bg-emerald-500' : inv.completion_percentage > 0 ? 'bg-indigo-500' : 'bg-slate-300'}`} style={{ width: `${inv.completion_percentage}%` }} />
                            </div>
                            <span className="text-xs font-medium text-slate-600">{inv.completion_percentage}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">{getStatusBadge(inv)}</td>
                        <td className="p-3 text-slate-500 text-xs">
                          {new Date(inv.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="View Details" onClick={() => setSelectedInvite(inv)} data-testid={`view-btn-${inv.id}`}>
                              <Eye size={14} />
                            </Button>
                            {inv.status !== 'revoked' && inv.status !== 'approved' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Copy Link" onClick={() => handleCopyLink(inv.portal_url)} data-testid={`copy-btn-${inv.id}`}>
                                <Copy size={14} />
                              </Button>
                            )}
                            {['pending', 'in_progress'].includes(inv.status) && !inv.is_expired && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Send Reminder" onClick={() => handleRemind(inv.id)}>
                                <Bell size={14} />
                              </Button>
                            )}
                            {inv.status === 'completed' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Approve & Sync" onClick={() => handleApprove(inv.id)} data-testid={`approve-btn-${inv.id}`}>
                                <ShieldCheck size={14} />
                              </Button>
                            )}
                            {inv.status !== 'approved' && inv.status !== 'revoked' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Revoke" onClick={() => handleRevoke(inv.id)}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Invite Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus size={18} className="text-indigo-500" /> New Onboarding Invite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Search Employee</Label>
              <Input placeholder="Search by name, ID, or email..." value={empSearch} onChange={e => setEmpSearch(e.target.value)} data-testid="emp-search-input" />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
              {availableEmployees.slice(0, 20).map(emp => {
                const name = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                return (
                  <button
                    key={emp.id}
                    className={`w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors flex items-center justify-between ${selectedEmpId === emp.id ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''}`}
                    onClick={() => setSelectedEmpId(emp.id)}
                    data-testid={`emp-option-${emp.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{name}</p>
                      <p className="text-xs text-slate-400">{emp.employee_id} {emp.email ? `| ${emp.email}` : ''}</p>
                    </div>
                    {selectedEmpId === emp.id && <CheckCircle size={16} className="text-indigo-600" />}
                  </button>
                );
              })}
              {availableEmployees.length === 0 && (
                <p className="p-4 text-sm text-center text-slate-400">No employees available</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="send-email" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="rounded" />
              <label htmlFor="send-email" className="text-sm text-slate-700 flex items-center gap-1">
                <Mail size={14} /> Send onboarding email to employee
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!selectedEmpId || creating} data-testid="send-invite-btn">
              {creating ? <Loader2 size={14} className="animate-spin mr-1" /> : <UserPlus size={14} className="mr-1" />}
              {sendEmail ? 'Send Invite' : 'Generate Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {selectedInvite && (
        <Dialog open={!!selectedInvite} onOpenChange={() => setSelectedInvite(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText size={18} className="text-indigo-500" />
                Onboarding Details: {selectedInvite.employee_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Info bar */}
              <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-3 rounded-lg">
                <Badge variant="outline">{selectedInvite.employee_code}</Badge>
                {selectedInvite.department_name && <Badge variant="outline">{selectedInvite.department_name}</Badge>}
                {selectedInvite.designation_name && <Badge variant="outline">{selectedInvite.designation_name}</Badge>}
                {getStatusBadge(selectedInvite)}
                <span className="text-xs text-slate-500 ml-auto">Progress: {selectedInvite.completion_percentage}%</span>
              </div>

              {/* Progress bar */}
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${selectedInvite.completion_percentage >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${selectedInvite.completion_percentage}%` }} />
              </div>

              {/* Submitted Data */}
              {Object.keys(selectedInvite.submitted_data || {}).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Submitted Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedInvite.submitted_data).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex justify-between p-2 bg-slate-50 rounded">
                        <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-slate-700">{k.includes('account') ? `XXXX${String(v).slice(-4)}` : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {(selectedInvite.documents || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Uploaded Documents</h4>
                  <div className="space-y-2">
                    {selectedInvite.documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} className="text-emerald-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{doc.doc_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                            <p className="text-xs text-slate-400">{doc.original_filename}</p>
                          </div>
                        </div>
                        <a href={`${window.location.origin}/api/onboarding/portal/${selectedInvite.token}/document/${doc.doc_type}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-xs flex items-center gap-1">
                          <ExternalLink size={12} /> View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Portal link */}
              <div className="bg-indigo-50 p-3 rounded-lg">
                <p className="text-xs text-indigo-600 font-medium mb-1">Portal Link</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-slate-600 flex-1 truncate">{selectedInvite.portal_url}</code>
                  <Button size="sm" variant="outline" onClick={() => handleCopyLink(selectedInvite.portal_url)} className="h-7">
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t">
                {selectedInvite.status === 'completed' && (
                  <Button onClick={() => handleApprove(selectedInvite.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dialog-approve-btn">
                    <ShieldCheck size={14} className="mr-1" /> Approve & Sync to Employee Record
                  </Button>
                )}
                {['pending', 'in_progress'].includes(selectedInvite.status) && !selectedInvite.is_expired && (
                  <Button variant="outline" onClick={() => handleRemind(selectedInvite.id)}>
                    <Bell size={14} className="mr-1" /> Send Reminder
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelectedInvite(null)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default OnboardingAdmin;
